import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns an EntityFormConfig for ERP Error Mapping create or edit mode.
 *
 * @param mode - 'create' or 'edit'
 * @param registrySlug - The slug of the parent registry
 * @param mappingSlug - Required for edit mode; the slug of the mapping being edited
 */
export function getErpErrorMappingFormConfig(
  mode: 'create' | 'edit',
  registrySlug: string,
  mappingSlug?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';
  const basePath = `/api/admin/erp/registries/${registrySlug}/error-mappings`;
  const mappingEndpoint = mappingSlug ? `${basePath}/${mappingSlug}` : basePath;

  return {
    id: isEdit ? createFormEditId('erp-error-mapping', mappingSlug ?? 'unknown') : createFormCreateId('erp-error-mapping'),

    endpoint: createFormEndpoint(
      isEdit,
      basePath,
      isEdit ? mappingEndpoint : basePath,
      !!mappingSlug,
      [
        'slug', 'priority', 'match_type', 'match_config',
        'origami_error_code', 'origami_error_message', 'http_status',
        'is_retryable', 'log_level',
      ],
    ),

    fields: {
      slug: {
        type: 'text',
        label: 'Slug',
        placeholder: 'e.g. auth-failed',
        helperText: 'Lowercase letters, numbers, and hyphens only',
        section: 'matching',
        order: 0,
        validation: {
          required: true,
          max: { value: 100, message: 'Slug must be 100 characters or fewer' },
          pattern: {
            value: '^[a-z0-9-]+$',
            message: 'Only lowercase letters, numbers, and hyphens are allowed',
          },
        },
      },
      priority: {
        type: 'number',
        label: 'Priority',
        helperText: 'Lower numbers are evaluated first',
        section: 'matching',
        order: 1,
        validation: {
          required: true,
          min: { value: 0, message: 'Priority must be 0 or greater' },
        },
        options: {
          min: 0,
          step: 1,
        },
      },
      match_type: {
        type: 'select',
        label: 'Match Type',
        section: 'matching',
        order: 2,
        validation: {
          required: true,
        },
        options: {
          items: [
            { value: 'http_status', label: 'HTTP Status' },
            { value: 'http_status_range', label: 'HTTP Status Range' },
            { value: 'error_code', label: 'Error Code' },
            { value: 'error_message', label: 'Error Message' },
            { value: 'body_contains', label: 'Body Contains' },
            { value: 'composite', label: 'Composite' },
          ],
        },
      },
      match_config: {
        type: 'textarea',
        label: 'Match Configuration (JSON)',
        placeholder: '{"status": 404}',
        section: 'matching',
        order: 3,
        validation: {
          required: true,
        },
        options: {
          rows: 5,
          resize: 'vertical',
        },
      },
      origami_error_code: {
        type: 'text',
        label: 'Error Code',
        placeholder: 'e.g. ERR_NOT_FOUND',
        section: 'mapping',
        order: 4,
        validation: {
          required: true,
          max: { value: 100, message: 'Error code must be 100 characters or fewer' },
        },
        options: {
          maxLength: 100,
        },
      },
      origami_error_message: {
        type: 'text',
        label: 'Error Message',
        placeholder: 'e.g. The requested resource was not found',
        section: 'mapping',
        order: 5,
        validation: {
          required: true,
          max: { value: 500, message: 'Error message must be 500 characters or fewer' },
        },
        options: {
          maxLength: 500,
        },
      },
      http_status: {
        type: 'number',
        label: 'HTTP Status Override',
        placeholder: 'e.g. 404',
        helperText: '400-599',
        section: 'mapping',
        order: 6,
        options: {
          min: 400,
          max: 599,
        },
      },
      is_retryable: {
        type: 'checkbox',
        label: 'Is Retryable',
        section: 'behavior',
        order: 7,
        defaultValue: false,
      },
      log_level: {
        type: 'select',
        label: 'Log Level',
        section: 'behavior',
        order: 8,
        defaultValue: 'error',
        options: {
          items: [
            { value: 'debug', label: 'Debug' },
            { value: 'info', label: 'Info' },
            { value: 'warning', label: 'Warning' },
            { value: 'error', label: 'Error' },
            { value: 'critical', label: 'Critical' },
          ],
        },
      },
    },

    sections: [
      {
        id: 'matching',
        title: 'Matching Rules',
        description: 'Define how errors are matched',
        order: 1,
      },
      {
        id: 'mapping',
        title: 'Error Mapping',
        description: 'Define the Origami error output',
        order: 2,
      },
      {
        id: 'behavior',
        title: 'Behavior',
        description: 'Configure retry and logging behavior',
        order: 3,
      },
    ],

    actions: createFormActions('Error Mapping', isEdit),

    workflow: createFormWorkflow(`/admin/erp/registries/${registrySlug}`),

    layout: createFormLayout(),
  };
}
